import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { LinkSimple, WarningCircle, ShieldWarning } from "@phosphor-icons/react";
import BOQItemPicker from "@/components/BOQItemPicker";
import Pagination from "@/components/Pagination";

const KIND_META = {
  po: { label: "Purchase Orders", labelSingular: "PO" },
  invoice: { label: "Invoices", labelSingular: "invoice" },
  outward: { label: "Outward Entries", labelSingular: "outward entry" },
  transfer: { label: "Transfers", labelSingular: "transfer" },
};

const fmt = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; } };

export default function BOQBulkLink() {
  const { activeProjectId, activeProject } = useProject();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [kind, setKind] = useState("po");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selected, setSelected] = useState(() => new Set());
  const [target, setTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!activeProjectId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/boq/unlinked", { params: { project_id: activeProjectId, kind, page, page_size: 50 } });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setSelected(new Set());
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeProjectId, kind, page]);

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    setSelected((prev) => {
      if (allSelected) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const handleLink = async () => {
    if (!target) { toast.error("Pick a BOQ item first."); return; }
    if (selected.size === 0) { toast.error("Select at least one row."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/boq/bulk-link", { kind, ids: [...selected], boq_item_id: target });
      toast.success(`${data.linked} ${KIND_META[kind].labelSingular}(s) linked`);
      await load();
      setTarget(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  if (!isAdmin) {
    return (
      <div className="p-12 text-center" data-testid="boq-bulk-link-guard">
        <ShieldWarning size={40} className="mx-auto text-neutral-300 mb-3" />
        <div className="font-display text-2xl font-medium">Owner Only</div>
        <div className="text-sm text-neutral-500 mt-2">Bulk-linking legacy records is restricted to admins.</div>
      </div>
    );
  }

  return (
    <div className="p-8 sm:p-10 max-w-[1400px] mx-auto" data-testid="boq-bulk-link-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2 flex items-center gap-2">
            {activeProject && (
              <>
                <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: activeProject.color || "#0047FF" }} />
                <span>{activeProject.name}</span>
                <span className="text-neutral-300">·</span>
              </>
            )}
            <span>Admin · BOQ Bulk-Link</span>
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-3">
            <LinkSimple size={30} className="text-primary" weight="fill" /> Link Legacy Records to BOQ
          </h1>
          <p className="text-sm text-neutral-500 mt-2">
            Attach a BOQ item to historical POs, invoices, outward entries, and transfers created before Phase 3 (mandatory linkage). Contract POs are excluded.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-4">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-neutral-500">Kind</label>
          <Select value={kind} onValueChange={(v) => { setKind(v); setPage(1); }}>
            <SelectTrigger className="rounded-sm h-10" data-testid="bulk-link-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(KIND_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="lg:col-span-2">
          <BOQItemPicker
            projectId={activeProjectId}
            value={target}
            onChange={(id) => setTarget(id)}
            required
            label="Attach to this BOQ item"
            testId="bulk-link-target"
          />
        </div>
        <div className="flex items-end">
          <Button
            className="w-full rounded-sm h-10 bg-primary hover:bg-[#0039CC] gap-1.5"
            disabled={saving || selected.size === 0 || !target}
            onClick={handleLink}
            data-testid="bulk-link-btn"
          >
            <LinkSimple size={14} weight="bold" /> {saving ? "Linking…" : `Link ${selected.size} record${selected.size !== 1 ? "s" : ""}`}
          </Button>
        </div>
      </div>

      <div className="border border-neutral-200 bg-white rounded-sm overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-200 bg-neutral-50">
          <label className="flex items-center gap-2 text-xs text-neutral-600">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="bulk-link-select-all" />
            Select all on this page ({rows.length})
          </label>
          <div className="text-xs text-neutral-500 tabular-nums">
            {total} unlinked {KIND_META[kind].labelSingular}(s) · showing {rows.length}
          </div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-neutral-400 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center">
            <WarningCircle size={36} className="mx-auto text-neutral-300 mb-3" />
            <div className="text-sm text-neutral-500">Nothing to link — every {KIND_META[kind].labelSingular} in this project is already attached to a BOQ item.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="w-8 px-3 py-2"></th>
                <th className="text-left px-3 py-2">Ref</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-right px-3 py-2">Qty / Amount</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">By</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <RowCells key={r.id} kind={kind} r={r} checked={selected.has(r.id)} onToggle={() => toggleOne(r.id)} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && <Pagination page={page} totalPages={totalPages} onChange={setPage} />}
    </div>
  );
}

function RowCells({ kind, r, checked, onToggle }) {
  let ref = "—";
  let qtyOrAmt = "—";
  if (kind === "po") { ref = r.po_number; qtyOrAmt = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(r.total_amount || 0); }
  else if (kind === "invoice") { ref = r.extracted_data?.invoice_number || r.original_filename || r.po_number; qtyOrAmt = "—"; }
  else if (kind === "outward") { ref = `${r.type || "outward"} · ${r.flat_number || r.site_location || ""}`; qtyOrAmt = `${fmt(r.quantity)} ${r.unit || ""}`; }
  else if (kind === "transfer") { ref = `→ ${r.to_project_name || "—"}`; qtyOrAmt = `${fmt(r.quantity)} ${r.unit || ""}`; }
  return (
    <tr className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`bulk-link-row-${r.id}`}>
      <td className="px-3 py-2"><input type="checkbox" checked={checked} onChange={onToggle} data-testid={`bulk-link-check-${r.id}`} /></td>
      <td className="px-3 py-2 font-medium text-neutral-900 text-xs">{ref || "—"}</td>
      <td className="px-3 py-2 text-neutral-700 text-xs">{r.category || "—"}</td>
      <td className="px-3 py-2 text-right tabular-nums text-xs">{qtyOrAmt}</td>
      <td className="px-3 py-2 text-neutral-500 text-xs">{fmtDate(r.created_at)}</td>
      <td className="px-3 py-2 text-neutral-500 text-xs">{r.created_by_name || "—"}</td>
    </tr>
  );
}
